import { ensureSystems } from "../sim/events";
import { retainMemory } from "../sim/reactions";
import { SAVE_VERSION, type WorldState } from "../sim/types";
import { migrate } from "./migrate";

const KEY = "cinderwell-save-v1";
const QUARANTINE = "cinderwell-save-quarantine";

export interface LoadReport {
  world: WorldState | null;
  from: number;
  steps: string[];
  problem?: string;
}

export function saveWorld(w: WorldState): void {
  const pack = {
    ...w,
    version: SAVE_VERSION,
    signals: [],
    // Decals cost 77 bytes each and this runs every 8 seconds. tickMemory
    // already washes and bounds them; this is insurance for a world that got
    // here another way — an old save, or a run that bled faster than it rained.
    // Pure, so saving never edits the world the player is standing in.
    memory: retainMemory(w.memory),
    overworld: { ...w.overworld, tiles: Array.from(w.overworld.tiles) },
    hollow: w.hollow ? { ...w.hollow, tiles: Array.from(w.hollow.tiles) } : null,
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(pack));
  } catch {
    /* quota — skip */
  }
}

export function loadWorldReport(): LoadReport {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return { world: null, from: 0, steps: [], problem: "storage unavailable" };
  }
  if (!raw) return { world: null, from: 0, steps: [] };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    quarantine(raw);
    return { world: null, from: 0, steps: [], problem: "save is not valid JSON" };
  }

  const res = migrate(parsed);
  if (!res.ok) {
    quarantine(raw);
    return { world: null, from: res.from, steps: [], problem: res.reason };
  }

  const w = res.world;
  w.overworld.tiles = Uint8Array.from(w.overworld.tiles as unknown as number[]);
  if (w.hollow) w.hollow.tiles = Uint8Array.from(w.hollow.tiles as unknown as number[]);
  ensureSystems(w);
  return { world: w, from: res.from, steps: res.steps };
}

export function loadWorld(): WorldState | null {
  return loadWorldReport().world;
}

function quarantine(raw: string): void {
  try {
    localStorage.setItem(QUARANTINE, raw);
  } catch {
    /* ignore */
  }
}

export function quarantinedSave(): string | null {
  try {
    return localStorage.getItem(QUARANTINE);
  } catch {
    return null;
  }
}

export function clearSave(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
